import React, { useMemo, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { ArrowForwardRounded } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";

/**
 * Reviews what the extraction found before anything is written.
 *
 * Every row is opt-in. Rows that would overwrite an existing value, or that
 * duplicate a list entry, arrive unchecked and are marked — an uploaded
 * document can belong to a different person, so the patient makes that call
 * rather than the model.
 *
 * With `sourceName` it reviews a record a hospital sent instead of documents.
 */
const ImportFromDocumentsDialog = ({
  open,
  onClose,
  loading,
  error,
  extraction,
  proposals,
  saving,
  onApply,
  sourceName,
}) => {
  const [overrides, setOverrides] = useState({});

  // Reset the checkbox overrides whenever a new extraction arrives, so a second
  // run doesn't inherit the previous run's selections.
  const proposalKey = proposals?.map((p) => p.id).join("|") ?? "";
  const [lastKey, setLastKey] = useState(proposalKey);
  if (proposalKey !== lastKey) {
    setLastKey(proposalKey);
    setOverrides({});
  }

  const isSelected = (proposal) =>
    overrides[proposal.id] ?? proposal.selected;

  const toggle = (proposal) =>
    setOverrides((prev) => ({
      ...prev,
      [proposal.id]: !(prev[proposal.id] ?? proposal.selected),
    }));

  const grouped = useMemo(() => {
    const map = new Map();
    (proposals || []).forEach((p) => {
      if (!map.has(p.group)) map.set(p.group, []);
      map.get(p.group).push(p);
    });
    return [...map.entries()];
  }, [proposals]);

  const selectedCount = (proposals || []).filter(isSelected).length;

  const handleApply = () => onApply((proposals || []).filter(isSelected));

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add to your MasterChart</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Stack alignItems="center" spacing={1.5} sx={{ py: 5 }}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary">
              Reading your documents…
            </Typography>
          </Stack>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <>
            {/* One paragraph: whose record this is and what to do. Model
                warnings sit under it, as things to look at while reviewing. */}
            {proposals?.length ? (
              <Typography variant="body2" sx={{ mb: 2 }}>
                {extraction?.demographics?.patientName ? (
                  <>
                    {sourceName ? `This record from ${sourceName} names` : "These documents name"}{" "}
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      {extraction.demographics.patientName}
                    </Box>
                    .{" "}
                  </>
                ) : null}
                Please review everything below to make sure it's about you and
                correct, and untick anything that isn't. Only ticked items are
                added.
              </Typography>
            ) : null}

            {extraction?.warnings?.length ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>A few things looked unusual</AlertTitle>
                <Stack spacing={0.75}>
                  {extraction.warnings.map((w, i) => (
                    <Typography variant="body2" key={i}>
                      {w}
                    </Typography>
                  ))}
                </Stack>
              </Alert>
            ) : null}

            {!proposals?.length ? (
              <Typography variant="body2" color="text.secondary">
                {sourceName
                  ? "Nothing new in this record. Your MasterChart already covers it."
                  : "Nothing new found in your documents. Your MasterChart already covers what they contain."}
              </Typography>
            ) : (
              grouped.map(([group, items], groupIndex) => (
                <Box key={group} sx={{ mt: groupIndex === 0 ? 0 : 2 }}>
                  <Typography variant="overline" color="text.secondary">
                    {group}
                  </Typography>
                  <Divider sx={{ mb: 1 }} />
                  {items.map((p) => (
                    <Box key={p.id} sx={{ mb: 1.5 }}>
                      <FormControlLabel
                        sx={{ alignItems: "flex-start", m: 0 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={isSelected(p)}
                            onChange={() => toggle(p)}
                            sx={{ pt: 0.25 }}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {p.label}
                            </Typography>
                            {/* A change reads as old value struck out, then
                                the new one; a duplicate just says so. No
                                chips: the line itself carries the meaning. */}
                            {p.conflict && p.current === "Already in your record" ? (
                              <Typography variant="body2" color="text.secondary">
                                {p.proposed ? `${p.proposed} · ` : ""}Already in your MasterChart
                              </Typography>
                            ) : p.conflict ? (
                              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ textDecoration: "line-through" }}
                                >
                                  {p.current}
                                </Typography>
                                <ArrowForwardRounded sx={{ fontSize: 16, color: "text.secondary" }} />
                                <Typography variant="body2">{p.proposed}</Typography>
                              </Stack>
                            ) : p.proposed ? (
                              <Typography variant="body2" color="text.secondary">
                                {p.proposed}
                              </Typography>
                            ) : null}
                            {p.sourceDocument ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                From {p.sourceDocument}
                              </Typography>
                            ) : null}
                          </Box>
                        }
                      />
                    </Box>
                  ))}
                </Box>
              ))
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Not now
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleApply}
          loading={saving}
          disabled={saving || loading || !selectedCount}
        >
          {selectedCount ? `Add ${selectedCount} item${selectedCount === 1 ? "" : "s"}` : "Add"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ImportFromDocumentsDialog;
